/**
 * The one money formatter for the whole site (WHO-5934).
 *
 * A dependency-free CJS leaf, like `config/locales.js`, for the same reason:
 * `next-i18next.config.js` needs it for `{{price, currency}}` and can only
 * `require()`, while app code must not import `next-i18next.config.js` (it
 * pulls `path` and the sprintf plugin into the client bundle). Both sides
 * import this file instead, so a translation string and a direct call give
 * the same text for the same amount and locale.
 *
 * It lays out the amount the API sends and never rounds it: the gateway and
 * search decide every final amount, a card's whole one included (price
 * display policy, WHO-5991 / WHO-5995).
 *
 * Rules, for every page:
 * - the locale's own CLDR shape: separators and € placement. Regional tags
 *   work as-is (`en-US` writes `$1,000.50`, `nl` writes `€ 1.000,50`), so a
 *   new market is a locale code, not a new formatter;
 * - the currency's own minor unit from ISO 4217, through `Intl`: two
 *   decimals for EUR, GBP, USD and CHF. Float noise below it is dropped
 *   (`925.004` is whole);
 * - where the amount is shown (`place`, see PRICE_PLACES) decides whether a
 *   whole amount keeps its cents and takes the locale's affix, today only
 *   the Dutch `,-` (`€ 925,-`);
 * - negatives use a real minus (U+2212) and non-breaking spaces, so the
 *   price never wraps across two lines;
 * - zero renders (`€0`); hiding it is the caller's choice;
 * - no locale means `en`.
 */

const FALLBACK_LOCALE = "en";
const FALLBACK_CURRENCY = "EUR";

/** Real minus (U+2212), not the ASCII hyphen that wraps away from the amount. */
const PRICE_MINUS = String.fromCharCode(0x2212);
const PRICE_NBSP = String.fromCharCode(0xa0);

const isWhole = amount => amount % 1 === 0;

/**
 * The four kinds of place in the price display policy (WHO-5991), and what
 * each does with a whole amount: `cents` "always" keeps them (`€925.00`),
 * `affixes` applies PRICE_PREFIX / PRICE_SUFFIX (the Dutch `,-`).
 *
 * - `list`: cards, search results, home, collections. `€ 450`, no `,-`. The
 *   API sends lists whole (`precision: WHOLE`); an amount that still has
 *   cents shows them, as sent.
 * - `exact` (default): product page, chat, My ads. Cents only when there are
 *   cents: `€ 925,-` / `€ 449,95`.
 * - `money`: cart, checkout, pay button, order, what a seller pays. Always
 *   cents, except where the Dutch `,-` stands in for them: `€925.00`,
 *   `€ 925,-`.
 * - `accounting`: payouts, My sales, sale details, earnings, and every payout
 *   breakdown (decision 6). Always cents, never `,-`: `€ 925,00`.
 */
const PRICE_PLACES = {
  list: { cents: "auto", affixes: false },
  exact: { cents: "auto", affixes: true },
  money: { cents: "always", affixes: true },
  accounting: { cents: "always", affixes: false },
};

/** @typedef {keyof typeof PRICE_PLACES} PricePlace */

const DEFAULT_PLACE = "exact";

/** ISO 4217 minor unit per currency, as `Intl` knows it (2 for EUR). */
const minorDigits = currency =>
  new Intl.NumberFormat(FALLBACK_LOCALE, {
    style: "currency",
    currency,
  }).resolvedOptions().maximumFractionDigits;

/**
 * Prefix per locale (exact tag first, then its base language). Beside the
 * value, a selector decides per amount whether it applies.
 */
const PRICE_PREFIX = {};

/** Suffix per locale, same shape as PRICE_PREFIX. */
const PRICE_SUFFIX = {
  nl: { value: ",-", selector: isWhole },
};

const toBaseLocale = locale => locale.split("-")[0];

const lookup = (config, locale) =>
  config?.[locale] ?? config?.[toBaseLocale(locale)];

const affix = (entry, amount) =>
  entry && (!entry.selector || entry.selector(amount)) ? entry.value ?? "" : "";

/**
 * @param {number | null | undefined} amount
 * @param {{
 *   locale?: string | null,
 *   currency?: string | null,
 *   place?: PricePlace,
 *   prefix?: Record<string, { value: string, selector?: (amount: number) => boolean }>,
 *   suffix?: Record<string, { value: string, selector?: (amount: number) => boolean }>,
 * }} [options]
 * @returns {string} empty for a missing or non-numeric amount
 * @throws {RangeError} for an unknown place or currency
 */
const formatMoney = (amount, options = {}) => {
  if (typeof amount !== "number" || Number.isNaN(amount)) {
    return "";
  }

  const placeName = options.place || DEFAULT_PLACE;
  const place = PRICE_PLACES[placeName];

  if (!place) {
    throw new RangeError(`Unknown price place: ${placeName}`);
  }

  const locale = options.locale || FALLBACK_LOCALE;
  const currency = options.currency || FALLBACK_CURRENCY;
  const digits = minorDigits(currency);
  const scale = 10 ** digits;
  const minor = Math.round(amount * scale);
  const absolute = Math.abs(minor) / scale;
  const whole = minor % scale === 0;

  const prefix = affix(
    lookup(options.prefix, locale) ??
      (place.affixes ? lookup(PRICE_PREFIX, locale) : undefined),
    absolute
  );
  const suffix = affix(
    lookup(options.suffix, locale) ??
      (place.affixes ? lookup(PRICE_SUFFIX, locale) : undefined),
    absolute
  );

  // A whole-amount suffix (`,-`) stands in for the cents, so it wins over
  // `cents: "always"`.
  const fractionDigits =
    whole && (place.cents !== "always" || suffix) ? 0 : digits;

  const number = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(absolute);

  const text = `${prefix}${number}${suffix}`.replace(/[ \u202f]/g, PRICE_NBSP);

  return minor < 0 ? `${PRICE_MINUS}${text}` : text;
};

module.exports = {
  formatMoney,
  PRICE_PLACES,
  PRICE_PREFIX,
  PRICE_SUFFIX,
  PRICE_MINUS,
  FALLBACK_LOCALE,
};
